/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Comparison helpers for playback URLs.
 *
 * Exists because the same stream is addressable by more than one spelling. A
 * packager URL carries its parameters as a comma-separated set inside a path
 * segment — `…/drm=widevine,group=TG_ALL,start=…,end=….mpd` — and a set has no
 * order, so the channel's tuning URI and the session's `playlistUrl` routinely
 * list the same parameters differently:
 *
 * ```
 * …/UnityTestChannel3/drm=widevine,group=TG_ALL,start=1789916400,end=1789920000.mpd
 * …/UnityTestChannel3/group=TG_ALL,drm=widevine,start=1789916400,end=1789920000.mpd
 * ```
 *
 * A plain `!==` calls those different and reloads the player onto a stream it
 * is already playing, which the viewer sees as a stall and a restart a second
 * or two into the programme.
 */
export class PlaybackUrlUtils {
  /** Separator between parameters inside a packager path segment. */
  private static readonly PARAMETER_SEPARATOR: string = ",";

  private static readonly KEY_VALUE_SEPARATOR: string = "=";

  /**
   * True when two URLs address the same stream.
   *
   * Equal only up to orderings that carry no meaning: the parameter set in a
   * path segment, the query-string parameters, and the case of the scheme and
   * host (which RFC 3986 defines as case-insensitive). Everything else is
   * compared literally — a differing path, a differing parameter value, or a
   * parameter present on one side and not the other all make the URLs
   * different, so a session URL that adds or changes anything still replaces
   * what is playing.
   *
   * Note on repeated query keys: parameters are compared as a sorted multiset
   * of `key=value` strings, so `a=1&a=2` and `a=2&a=1` compare equal. No
   * playback URL here depends on the relative order of two values for one key.
   */
  public static areEquivalent(
    first: string | null | undefined,
    second: string | null | undefined
  ): boolean {
    const left: string = first !== undefined && first !== null ? first : "";
    const right: string = second !== undefined && second !== null ? second : "";

    if (left === right) {
      return true;
    }

    if (left === "" || right === "") {
      return false;
    }

    return (
      PlaybackUrlUtils.normalize(left) === PlaybackUrlUtils.normalize(right)
    );
  }

  /** Rewrites a URL to a form in which meaningless orderings are fixed. */
  private static normalize(url: string): string {
    const fragmentIndex: number = url.indexOf("#");
    const withoutFragment: string =
      fragmentIndex === -1 ? url : url.slice(0, fragmentIndex);

    const queryIndex: number = withoutFragment.indexOf("?");

    if (queryIndex === -1) {
      return PlaybackUrlUtils.normalizePath(withoutFragment);
    }

    const path: string = withoutFragment.slice(0, queryIndex);
    const query: string = withoutFragment.slice(queryIndex + 1);

    return (
      PlaybackUrlUtils.normalizePath(path) +
      "?" +
      PlaybackUrlUtils.normalizeQuery(query)
    );
  }

  /**
   * Lower-cases the scheme and host, and sorts the parameter set inside every
   * path segment that carries one. Path case is left alone: only the scheme
   * and host are case-insensitive, and a channel segment such as
   * `UnityTestChannel3` is not.
   */
  private static normalizePath(url: string): string {
    const schemeIndex: number = url.indexOf("://");

    if (schemeIndex === -1) {
      return PlaybackUrlUtils.normalizeSegments(url);
    }

    const pathIndex: number = url.indexOf("/", schemeIndex + 3);

    if (pathIndex === -1) {
      return url.toLowerCase();
    }

    return (
      url.slice(0, pathIndex).toLowerCase() +
      PlaybackUrlUtils.normalizeSegments(url.slice(pathIndex))
    );
  }

  private static normalizeSegments(path: string): string {
    return path
      .split("/")
      .map((segment: string) => PlaybackUrlUtils.normalizeSegment(segment))
      .join("/");
  }

  /**
   * Sorts one path segment's parameter set, if it has one.
   *
   * A segment qualifies only when every comma-separated part is a `key=value`
   * pair, so an ordinary path segment that merely contains a comma is left
   * untouched rather than reordered into something the origin would not serve.
   *
   * The file extension rides on whichever parameter happens to be last
   * (`…,end=1789920000.mpd`), so it is detached before sorting and reattached
   * after — otherwise two orderings of the same set would sort differently,
   * each carrying `.mpd` on a different parameter.
   */
  private static normalizeSegment(segment: string): string {
    const separatorIndex: number = segment.lastIndexOf(
      PlaybackUrlUtils.PARAMETER_SEPARATOR
    );

    if (separatorIndex === -1) {
      return segment;
    }

    const dotIndex: number = segment.lastIndexOf(".");
    const hasExtension: boolean = dotIndex > separatorIndex;
    const extension: string = hasExtension ? segment.slice(dotIndex) : "";
    const body: string = hasExtension ? segment.slice(0, dotIndex) : segment;

    const parameters: string[] = body.split(
      PlaybackUrlUtils.PARAMETER_SEPARATOR
    );

    const isParameterSet: boolean = parameters.every(
      (parameter: string) =>
        parameter.indexOf(PlaybackUrlUtils.KEY_VALUE_SEPARATOR) > 0
    );

    if (!isParameterSet) {
      return segment;
    }

    return (
      [...parameters]
        .sort(PlaybackUrlUtils.compareCodeUnits)
        .join(PlaybackUrlUtils.PARAMETER_SEPARATOR) + extension
    );
  }

  /** Sorts the query parameters, which carry no order of their own. */
  private static normalizeQuery(query: string): string {
    return query.split("&").sort(PlaybackUrlUtils.compareCodeUnits).join("&");
  }

  /**
   * Orders two strings by UTF-16 code unit — the ordering `Array.sort()`
   * applies by default, stated explicitly.
   *
   * Not `localeCompare`: its order depends on the runtime's locale, which
   * would let one device normalize `a=1,B=2` one way and another device the
   * other, so two URLs could compare equivalent on one device and not on the
   * next. Only the ordering's stability matters here — the sorted form is
   * never shown or requested, just compared — so a locale-independent one is
   * the only safe choice.
   */
  private static compareCodeUnits(first: string, second: string): number {
    if (first < second) {
      return -1;
    }

    if (first > second) {
      return 1;
    }

    return 0;
  }
}
